/**
 * Bootstrap 5.3 color mode (`data-bs-theme`) + localStorage persistence.
 * @see https://getbootstrap.com/docs/5.3/customize/color-modes/
 */

// Persisted theme — also read synchronously in `public/index.html` for splash paint (keep key in sync).
const STORAGE_KEY = 'augmentt-bs-theme'

// Match `public/css/splash-screen.css` splash / UA chrome tint.
const THEME_COLOR_LIGHT = '#f7f8fa'
const THEME_COLOR_DARK = '#0e1623'

/** Fired on `<html data-bs-theme>` changes so components can re-read the theme (e.g. header logo). */
export const BS_THEME_CHANGE_EVENT = 'augmentt:bs-theme'

/** @param {unknown} value @returns {'light' | 'dark'} */
function normalizeMode(value) {
  return value === 'dark' ? 'dark' : 'light'
}

function syncMetronicSkins(isDark) {
  if (typeof document === 'undefined') {
    return
  }
  // Metronic: body classes `aside-light` / `aside-dark` alongside `data-bs-theme`.
  document.body.classList.remove('aside-light', 'aside-dark')
  document.body.classList.add(isDark ? 'aside-dark' : 'aside-light')
}

/** @returns {'light' | 'dark'} */
export function getStoredColorScheme() {
  if (typeof window === 'undefined') {
    return 'light'
  }
  try {
    return normalizeMode(localStorage.getItem(STORAGE_KEY))
  } catch {
    return 'light'
  }
}

/** @param {'light' | 'dark'} mode */
export function applyColorScheme(mode) {
  if (typeof document === 'undefined') {
    return
  }
  const m = normalizeMode(mode)
  const isDark = m === 'dark'

  document.documentElement.setAttribute('data-bs-theme', m)
  document.documentElement.style.colorScheme = isDark ? 'dark' : 'light'

  const themeMeta = document.querySelector('meta[name="theme-color"]')
  if (themeMeta) {
    themeMeta.setAttribute('content', isDark ? THEME_COLOR_DARK : THEME_COLOR_LIGHT)
  }

  syncMetronicSkins(isDark)

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(BS_THEME_CHANGE_EVENT, { detail: { mode: m } }))
  }
}

/** @param {'light' | 'dark'} mode */
export function setStoredColorScheme(mode) {
  const m = normalizeMode(mode)
  // Persist before `apply`: listeners may call `getStoredColorScheme()` in the same tick.
  try {
    localStorage.setItem(STORAGE_KEY, m)
  } catch {
    // quota / private mode
  }
  applyColorScheme(m)
}

/** Call once at app startup (before or after first paint). */
export function initColorSchemeFromStorage() {
  applyColorScheme(getStoredColorScheme())
}
